import { SparklesIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useContext, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import UserContext from "context/UserContext";

import Button from "components/atoms/Button";
import Card from "components/atoms/Card";
import Container from "components/atoms/Container";
import Modal from "components/atoms/Modal";
import NavHeader from "components/organisms/NavHeader";

import { error } from "utils";
import {
  AI_HUB_SLUG,
  MOBILE_AI_SLUG,
  NUTRITION_ADVISOR_SLUG,
  NUTRITION_AI_SLUG,
  NUTRITION_API_SLUG,
  NUTRITION_PLATFORM_SLUG,
  PASSIO_HUB_SLUG,
  REMODEL_AI_SLUG,
  VR_SHOWROOM_SLUG,
} from "utils/products";
import { CustomerStatusType } from "utils/types";

import MobileAICardImage from "images/mobile-ai.png";
import NutritionAdvisorCardImage from "images/nutrition-advisor.png";
import NutritionAICardImage from "images/nutrition-ai.png";
import NutritionAPICardImage from "images/nutrition-api.png";
import RemodelAICardImage from "images/remodel-ai.png";
import VRShowroomCardImage from "images/vr-showroom.png";

interface MobileDataTypes {
  isSubscribed: boolean;
  isValidSubscription: boolean;
}

interface NutritionDataTypes {
  isSubscribed: boolean;
  isValidSubscription: boolean;
}

interface NutritionAPIDataTypes {
  isSubscribed: boolean;
  isValidSubscription: boolean;
}

interface NutritionAdvisorDataTypes {
  isSubscribed: boolean;
  isValidSubscription: boolean;
}

interface RemodelAIDataTypes {
  isSubscribed: boolean;
  isValidSubscription: boolean;
}

interface VRShowroomDataTypes {
  isSubscribed: boolean;
  isValidSubscription: boolean;
}

interface UnifiedTypes {
  isSubscribed: boolean;
  isValidSubscription: boolean;
}

interface TieredAIHubTypes {
  isSubscribed: boolean;
  isValidSubscription: boolean;
  tier: string | null;
}

interface PlatformTypes {
  isSubscribed: boolean;
  isValidSubscription: boolean;
  tier: string | null;
}

type PlatformTierKey = "starter" | "growth" | "pro";

type PlatformTierConfig = {
  label: string;
  includedTokens: string;
  refillRate: string;
  insights: boolean;
  aiInsights: boolean;
};

const PLATFORM_TIER_CONFIG: Record<PlatformTierKey, PlatformTierConfig> = {
  starter: {
    label: "Starter",
    includedTokens: "1M",
    refillRate: "$25 / 1M",
    insights: false,
    aiInsights: false,
  },
  growth: {
    label: "Growth",
    includedTokens: "5M",
    refillRate: "$10 / 1M",
    insights: true,
    aiInsights: false,
  },
  pro: {
    label: "Pro",
    includedTokens: "100M",
    refillRate: "$2.50 / 1M",
    insights: true,
    aiInsights: true,
  },
};

const normalizePlatformTier = (
  tierValue: string | null | undefined
): PlatformTierKey | null => {
  const value = String(tierValue || "")
    .trim()
    .toLowerCase();
  if (!value) return null;
  if (value === "starter" || value.includes("starter")) return "starter";
  if (value === "growth" || value.includes("growth")) return "growth";
  if (value === "pro" || value.includes("pro")) return "pro";
  return null;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const flags = useFlags();

  // Feature flags for controlling product visibility
  const showMobileAIPlatform = false; // Set to true to show Mobile AI Platform card
  // TODO: WARNING - Set to true when v2 billing is enabled and tiered AI Hub is ready for production
  const showTieredAIHub = false; // Set to true to show Tiered AI Hub card

  const [displayIndividualLegacySubs, setDisplayIndividualLegacySubs] =
    useState(false);

  const [unauthorized, setUnauthorized] = useState(false);
  const [, setCustomerStatus] = useState<CustomerStatusType>({
    hasOrg: false,
    hasPaymentMethod: false,
  });
  const [mobileData, setMobileData] = useState<MobileDataTypes>({
    isSubscribed: false,
    isValidSubscription: false,
  });
  const [nutritionData, setNutriritionData] = useState<NutritionDataTypes>({
    isSubscribed: false,
    isValidSubscription: false,
  });
  const [nutritionAPIData, setNutriritionAPIData] =
    useState<NutritionAPIDataTypes>({
      isSubscribed: false,
      isValidSubscription: false,
    });
  const [nutritionAdvisorData, setNutritionAdvisorData] =
    useState<NutritionAdvisorDataTypes>({
      isSubscribed: false,
      isValidSubscription: false,
    });
  const [remodelAIData, setRemodelAIData] = useState<RemodelAIDataTypes>({
    isSubscribed: false,
    isValidSubscription: false,
  });
  const [vrShowroomData, setVRShowroomData] = useState<VRShowroomDataTypes>({
    isSubscribed: false,
    isValidSubscription: false,
  });
  const [unifiedData, setUnifiedData] = useState<UnifiedTypes>({
    isSubscribed: false,
    isValidSubscription: false,
  });
  const [tieredAIHubData, setTieredAIHubData] = useState<TieredAIHubTypes>({
    isSubscribed: false,
    isValidSubscription: false,
    tier: null,
  });
  const [platformData, setPlatformData] = useState<PlatformTypes>({
    isSubscribed: false,
    isValidSubscription: false,
    tier: null,
  });
  const isDevEnvironment =
    String(process.env.REACT_APP_ENV || "")
      .trim()
      .toLowerCase() !== "production";
  const hubSignupEnabled = isDevEnvironment || !!flags["nutrition-ai-hub"];
  const showNutritionAIHubCard =
    !platformData.isSubscribed &&
    (unifiedData.isSubscribed || hubSignupEnabled);

  const invalidMobileSubscription =
    mobileData.isSubscribed && !mobileData.isValidSubscription;
  const invalidNutritionSubscription =
    nutritionData.isSubscribed && !nutritionData.isValidSubscription;
  const invalidNutritionAPISubscription =
    nutritionAPIData.isSubscribed && !nutritionAPIData.isValidSubscription;
  const invalidNutritionAdvisorSubscription =
    nutritionAdvisorData.isSubscribed &&
    !nutritionAdvisorData.isValidSubscription;
  const invalidRemodelAISubscription =
    remodelAIData.isSubscribed && !remodelAIData.isValidSubscription;
  const invalidVRShowroomSubscription =
    vrShowroomData.isSubscribed && !vrShowroomData.isValidSubscription;
  const invalidUnifiedSubscription =
    unifiedData.isSubscribed && !unifiedData.isValidSubscription;
  const invalidTieredAIHubSubscription =
    tieredAIHubData.isSubscribed && !tieredAIHubData.isValidSubscription;
  const invalidPlatformSubscription =
    platformData.isSubscribed && !platformData.isValidSubscription;

  const [invalidSubscriptionModalIsOpen, setInvalidSubscriptionModalIsOpen] =
    useState(false);

  const userContext = useContext(UserContext);

  const getProducts = async () => await axios.get(`/accounts/products`);

  const { isLoading, isFetching, isPaused } = useQuery(
    ["products"],
    getProducts,
    {
      // Allow query to run even for new users without a customerid
      // The backend handles this case gracefully by returning empty subscriptions
      enabled: !!userContext,
      onError: (err: AxiosError) => {
        error(err?.response?.data?.cause || "Could not fetch the products");
        if (err?.response?.status === 403) {
          setUnauthorized(true);
        }
      },
      onSuccess: (res: AxiosResponse) => {
        setCustomerStatus((prev) => ({
          ...prev,
          hasOrg: res?.data.hasOrg,
          hasPaymentMethod: res?.data.hasPaymentMethod,
        }));
        setNutriritionData((prev) => ({
          ...prev,
          isSubscribed: res?.data.nutritionAI.isSubscribed,
          isValidSubscription: res?.data.nutritionAI.isValidSubscription,
        }));
        setNutriritionAPIData((prev) => ({
          ...prev,
          isSubscribed: res?.data.nutritionAPI.isSubscribed,
          isValidSubscription: res?.data.nutritionAPI.isValidSubscription,
        }));
        setNutritionAdvisorData((prev) => ({
          ...prev,
          isSubscribed: res?.data.nutritionAdvisor.isSubscribed,
          isValidSubscription: res?.data.nutritionAdvisor.isValidSubscription,
        }));
        setRemodelAIData((prev) => ({
          ...prev,
          isSubscribed: res?.data.remodelAI.isSubscribed,
          isValidSubscription: res?.data.remodelAI.isValidSubscription,
        }));
        setVRShowroomData((prev) => ({
          ...prev,
          isSubscribed: res?.data.vrShowroom.isSubscribed,
          isValidSubscription: res?.data.vrShowroom.isValidSubscription,
        }));
        setMobileData((prev) => ({
          ...prev,
          isSubscribed: res?.data.mobileAI.isSubscribed,
          isValidSubscription: res?.data.mobileAI.isValidSubscription,
        }));
        const dynamics = res?.data.dynamicVolumeSubscriptions || [];
        // TODO: DVS: Cleanup
        const selectedItem = dynamics.find(
          (item: any) => item?.info?.PackageName === "Nutrition Hub"
        );
        setUnifiedData((prev) => ({
          ...prev,
          isSubscribed: selectedItem?.isSubscribed,
          isValidSubscription: selectedItem?.isValidSubscription,
        }));

        // Check for Tiered AI Hub subscription
        const tieredAIHubItem = dynamics.find(
          (item: any) => item?.info?.PackageName === "AI Hub"
        );
        if (tieredAIHubItem) {
          setTieredAIHubData((prev) => ({
            ...prev,
            isSubscribed: tieredAIHubItem?.isSubscribed,
            isValidSubscription: tieredAIHubItem?.isValidSubscription,
            tier: tieredAIHubItem?.info?.tier || null,
          }));
        }

        // [Platform Product] Check for Nutrition AI Platform subscription
        const platformItem = dynamics.find(
          (item: any) => item?.info?.PackageName === "Nutrition Platform"
        );
        if (platformItem) {
          setPlatformData((prev) => ({
            ...prev,
            isSubscribed: platformItem?.isSubscribed,
            isValidSubscription: platformItem?.isValidSubscription,
            tier: platformItem?.stripeSubscription?.metadata?.tier || null,
          }));
        }

        if (
          res?.data?.nutritionAI?.isSubscribed ||
          res?.data?.nutritionAPI?.isSubscribed ||
          res?.data?.nutritionAdvisor?.isSubscribed
        ) {
          setDisplayIndividualLegacySubs(true);
        }
      },
    }
  );
  const isWaiting = isFetching || isLoading || isPaused;

  if (unauthorized) {
    return <Navigate to="/access-restricted" />;
  }

  const platformTierKey = normalizePlatformTier(platformData.tier);
  const platformTier = platformTierKey
    ? PLATFORM_TIER_CONFIG[platformTierKey]
    : null;
  const insightsEnabled = !!platformTier?.insights;
  const aiInsightsEnabled = !!platformTier?.aiInsights;

  const showOtherProducts =
    displayIndividualLegacySubs ||
    showMobileAIPlatform ||
    flags["remodel-ai-purchase"] ||
    flags["vr-showroom-purchase"];

  return (
    <NavHeader>
      <Container>
        <h2 className="font-bold inter-bold text-4xl mb-1">Your Dashboard</h2>
        <hr className="bg-passio-gray1 mb-8"></hr>

        {/* ================================================================
            ROW 1 — PLATFORM HERO (full-width featured product)
            ================================================================ */}
        {flags["nutrition-ai-platform"] && (
          <div className="mb-6">
            <div className="rounded-xl overflow-hidden shadow-lg border border-indigo-100">
              {/* Gradient banner */}
              <div className="relative overflow-hidden bg-gradient-to-r from-sky-700 via-indigo-700 to-blue-800 px-6 py-8 sm:px-10 sm:py-10">
                <div className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-white/10 blur-2xl pointer-events-none" />
                <div className="absolute -bottom-24 left-8 h-56 w-56 rounded-full bg-cyan-300/10 blur-3xl pointer-events-none" />
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-white text-2xl font-extrabold tracking-tight">
                        Nutrition AI Platform
                      </h3>
                      <span className="bg-white/20 backdrop-blur text-white text-xs font-bold px-2.5 py-0.5 rounded-full">
                        NEW
                      </span>
                      {platformData.tier && (
                        <span className="bg-white/20 backdrop-blur text-white text-xs font-semibold px-3 py-0.5 rounded-full">
                          {(platformTier?.label ||
                            platformData.tier.charAt(0).toUpperCase() +
                              platformData.tier.slice(1)) + " "}
                          Plan
                        </span>
                      )}
                    </div>
                    <p className="text-indigo-100 text-base max-w-2xl leading-relaxed">
                      {platformData.isSubscribed
                        ? "Your all-in-one nutrition AI subscription — food recognition API, mobile SDKs, Easy AI builder, route-level insights, and model customization."
                        : "Choose Starter, Growth, or Pro for access to our food recognition API, mobile SDKs, Easy AI builder, route-level insights, model customization, and more — all with included monthly tokens."}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <span className="bg-white/15 text-white text-xs font-medium px-2.5 py-1 rounded-full">
                        API + SDK
                      </span>
                      <span className="bg-white/15 text-white text-xs font-medium px-2.5 py-1 rounded-full">
                        Easy AI Builder
                      </span>
                      <span className="bg-white/15 text-white text-xs font-medium px-2.5 py-1 rounded-full">
                        Route Analytics
                      </span>
                      <span className="bg-white/15 text-white text-xs font-medium px-2.5 py-1 rounded-full">
                        Tiered Refill Pricing
                      </span>
                    </div>
                  </div>
                  {!platformData.isSubscribed && (
                    <a
                      href={
                        process.env.REACT_APP_MARKETING_BASE + "/nutrition-ai"
                      }
                      target="_blank"
                      rel="noreferrer"
                      className="text-indigo-100 hover:text-white text-sm underline underline-offset-2 whitespace-nowrap"
                    >
                      Learn more
                    </a>
                  )}
                </div>
              </div>

              {/* Action area */}
              <div className="bg-white px-6 py-5 sm:px-10">
                {invalidPlatformSubscription ? (
                  <Button
                    text="Subscription Issue — Contact Support"
                    fullWidth
                    isWarning
                    isLoading={isWaiting}
                    disabled={isWaiting}
                    onClick={() => setInvalidSubscriptionModalIsOpen(true)}
                  />
                ) : platformData.isSubscribed ? (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                        <p className="text-xs text-slate-500">Your tier</p>
                        <p className="text-sm font-semibold text-slate-800">
                          {platformTier?.label || "Active"}
                        </p>
                      </div>
                      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                        <p className="text-xs text-slate-500">
                          Included / month
                        </p>
                        <p className="text-sm font-semibold text-slate-800">
                          {platformTier?.includedTokens || "—"}
                        </p>
                      </div>
                      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                        <p className="text-xs text-slate-500">Refill rate</p>
                        <p className="text-sm font-semibold text-slate-800">
                          {platformTier?.refillRate || "—"}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                      <button
                        onClick={() =>
                          navigate(
                            `/details?product=${NUTRITION_PLATFORM_SLUG}&tab=account`
                          )
                        }
                        disabled={isWaiting}
                        className="group flex flex-col items-center gap-2 rounded-lg border-2 border-indigo-100 bg-white hover:bg-indigo-50 hover:border-indigo-300 px-4 py-4 shadow-sm hover:shadow transition-all disabled:opacity-50"
                      >
                        <svg
                          className="w-6 h-6 text-indigo-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
                          />
                        </svg>
                        <span className="text-sm font-medium text-gray-800">
                          Account Info
                        </span>
                      </button>
                      <button
                        onClick={() =>
                          navigate(
                            `/details?product=${NUTRITION_PLATFORM_SLUG}&tab=easyai`
                          )
                        }
                        disabled={isWaiting}
                        className="group flex flex-col items-center gap-2 rounded-lg border-2 border-purple-100 bg-white hover:bg-purple-50 hover:border-purple-300 px-4 py-4 shadow-sm hover:shadow transition-all disabled:opacity-50"
                      >
                        <svg
                          className="w-6 h-6 text-purple-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z"
                          />
                        </svg>
                        <span className="text-sm font-medium text-gray-800">
                          Easy AI
                        </span>
                      </button>
                      <button
                        onClick={() => {
                          if (!insightsEnabled) return;
                          navigate(
                            `/details?product=${NUTRITION_PLATFORM_SLUG}&tab=insights`
                          );
                        }}
                        disabled={isWaiting || !insightsEnabled}
                        title={
                          !insightsEnabled
                            ? "Upgrade to Growth or Pro to unlock Insights."
                            : undefined
                        }
                        className={`group flex flex-col items-center gap-2 rounded-lg border-2 px-4 py-4 shadow-sm hover:shadow transition-all disabled:opacity-60 ${
                          insightsEnabled
                            ? "border-emerald-100 bg-white hover:bg-emerald-50 hover:border-emerald-300"
                            : "border-gray-200 bg-gray-100 cursor-not-allowed"
                        }`}
                      >
                        <svg
                          className="w-6 h-6 text-emerald-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v-5.5m3 5.5v-3.5m3 3.5v-1.5"
                          />
                        </svg>
                        <span className="text-sm font-medium text-gray-800">
                          Insights
                        </span>
                      </button>
                      <button
                        onClick={() => {
                          if (!aiInsightsEnabled) return;
                          navigate(
                            `/details?product=${NUTRITION_PLATFORM_SLUG}&tab=insights&insightMode=ai`
                          );
                        }}
                        disabled={isWaiting || !aiInsightsEnabled}
                        title={
                          !aiInsightsEnabled
                            ? "AI Insights are available on Pro."
                            : undefined
                        }
                        className={`group flex flex-col items-center gap-2 rounded-lg border-2 px-4 py-4 shadow-sm hover:shadow transition-all disabled:opacity-60 ${
                          aiInsightsEnabled
                            ? "border-violet-100 bg-white hover:bg-violet-50 hover:border-violet-300"
                            : "border-gray-200 bg-gray-100 cursor-not-allowed"
                        }`}
                      >
                        <SparklesIcon
                          className={`w-6 h-6 ${
                            aiInsightsEnabled
                              ? "text-violet-600"
                              : "text-gray-400"
                          }`}
                        />
                        <span className="text-sm font-medium text-gray-800">
                          AI Insights
                        </span>
                      </button>
                      <button
                        onClick={() =>
                          navigate(
                            `/manage-subscription?product=${NUTRITION_PLATFORM_SLUG}`
                          )
                        }
                        disabled={isWaiting}
                        className="group flex flex-col items-center gap-2 rounded-lg border-2 border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 px-4 py-4 shadow-sm hover:shadow transition-all disabled:opacity-50"
                      >
                        <svg
                          className="w-6 h-6 text-gray-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        <span className="text-sm font-medium text-gray-800">
                          Manage Plan
                        </span>
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      text="View Plans & Pricing"
                      fullWidth
                      isLoading={isWaiting}
                      disabled={isWaiting}
                      onClick={() =>
                        navigate(`/details?product=${NUTRITION_PLATFORM_SLUG}`)
                      }
                    />
                    <Button
                      text="Learn More"
                      outlined
                      fullWidth
                      onClick={() =>
                        window.open(
                          process.env.REACT_APP_MARKETING_BASE +
                            "/nutrition-ai",
                          "_blank"
                        )
                      }
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ================================================================
            ROW 2 — NUTRITION AI HUB
            Show when subscribed, or when `nutrition-ai-hub` feature flag is enabled.
            ================================================================ */}
        {showNutritionAIHubCard && (
          <div className="mb-6">
            <Card className="!flex-row flex-wrap sm:flex-nowrap items-center gap-4 sm:gap-6">
              <div className="flex-shrink-0 w-full sm:w-48">
                <img
                  src={NutritionAICardImage}
                  alt="Nutrition AI Hub"
                  className="object-cover rounded-md w-full"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-extrabold text-lg">Nutrition AI Hub</p>
                <p className="text-sm text-gray-500 mt-1">
                  Your credit-based subscription to all nutrition products.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                {invalidUnifiedSubscription ? (
                  <Button
                    text="Subscription Issue"
                    isWarning
                    isLoading={isWaiting}
                    disabled={isWaiting}
                    onClick={() => setInvalidSubscriptionModalIsOpen(true)}
                  />
                ) : (
                  <>
                    {unifiedData.isSubscribed ? (
                      <>
                        <Button
                          text="Hub Details"
                          outlined
                          isLoading={isWaiting}
                          disabled={isWaiting}
                          onClick={() =>
                            navigate(`/details?product=${PASSIO_HUB_SLUG}`)
                          }
                        />
                        <Button
                          text="Manage Subscription"
                          outlined
                          isLoading={isWaiting}
                          disabled={isWaiting}
                          onClick={() =>
                            navigate(
                              `/manage-subscription?product=${PASSIO_HUB_SLUG}`
                            )
                          }
                        />
                      </>
                    ) : (
                      <Button
                        text="View Hub Plans"
                        outlined
                        isLoading={isWaiting}
                        disabled={isWaiting}
                        onClick={() =>
                          navigate(
                            `/manage-subscription?product=${PASSIO_HUB_SLUG}`
                          )
                        }
                      />
                    )}
                  </>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* ================================================================
            ROW 3 — OTHER PRODUCTS (grid, smaller cards)
            ================================================================ */}
        {showOtherProducts && (
          <>
            <h3 className="font-bold text-xl mt-4 mb-3 text-gray-700">
              Other Products
            </h3>
            <div className="grid lg:grid-cols-6 gap-4">
              {/* Tiered AI Hub (hidden until enabled) */}
              {showTieredAIHub && (
                <Card className="col-span-2">
                  <p className="font-extrabold">AI Hub (Tiered Plans)</p>
                  <span className="text-sm text-gray-500 pt-1 pb-2">
                    Flexible tier-based AI subscription
                  </span>
                  <p className="mt-1 mb-6 text-sm leading-6 text-gray-900 sm:mt-2">
                    Choose from Trial, Professional, or Enterprise tiers. Start
                    small and upgrade as your usage grows!
                  </p>
                  <img
                    src={NutritionAICardImage}
                    alt="AI Hub"
                    className="object-cover rounded-md"
                  />
                  {tieredAIHubData.tier && (
                    <div className="text-center my-3">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                        Current:{" "}
                        {tieredAIHubData.tier.charAt(0).toUpperCase() +
                          tieredAIHubData.tier.slice(1)}{" "}
                        Tier
                      </span>
                    </div>
                  )}
                  <div className="mt-auto pt-4">
                    <Button
                      text={
                        invalidTieredAIHubSubscription
                          ? "Subscription Issue"
                          : tieredAIHubData.isSubscribed
                          ? "Product Details"
                          : "Subscribe"
                      }
                      outlined
                      fullWidth
                      isWarning={invalidTieredAIHubSubscription}
                      isLoading={isWaiting}
                      disabled={isWaiting}
                      onClick={() =>
                        invalidTieredAIHubSubscription
                          ? setInvalidSubscriptionModalIsOpen(true)
                          : tieredAIHubData.isSubscribed
                          ? navigate(`/details?product=${AI_HUB_SLUG}`)
                          : navigate(
                              `/manage-subscription?product=${AI_HUB_SLUG}`
                            )
                      }
                    />
                  </div>
                </Card>
              )}

              {/* Legacy: Nutrition-AI Mobile SDK */}
              {displayIndividualLegacySubs && (
                <Card className="col-span-2">
                  <p className="font-extrabold">Nutrition-AI Mobile SDK</p>
                  <span className="text-sm text-gray-500 pt-1 pb-2">
                    Mobile Application Development SDK
                  </span>
                  <p className="mt-1 mb-6 text-sm leading-6 text-gray-900 sm:mt-2">
                    Quickly create or supercharge existing mobile applications
                    with computer vision, searchable foods, logging, and more!
                  </p>
                  <img
                    src={NutritionAICardImage}
                    alt="Nutrition AI"
                    className="object-cover rounded-md"
                  />
                  <div className="mt-auto pt-4">
                    <Button
                      text={
                        invalidNutritionSubscription
                          ? "Subscription Issue"
                          : nutritionData.isSubscribed
                          ? "Product Details"
                          : "Subscribe"
                      }
                      outlined
                      fullWidth
                      isWarning={invalidNutritionSubscription}
                      isLoading={isWaiting}
                      disabled={isWaiting}
                      onClick={() =>
                        invalidNutritionSubscription
                          ? setInvalidSubscriptionModalIsOpen(true)
                          : nutritionData.isSubscribed
                          ? navigate(`/details?product=${NUTRITION_AI_SLUG}`)
                          : navigate(
                              `/manage-subscription?product=${NUTRITION_AI_SLUG}`
                            )
                      }
                    />
                  </div>
                </Card>
              )}

              {/* Legacy: Food Database API */}
              {displayIndividualLegacySubs && (
                <Card className="col-span-2">
                  <p className="font-extrabold">Food Database API</p>
                  <span className="text-sm text-gray-500 pt-1 pb-2">
                    (RESTful) API with over 2.3 million possibilities
                  </span>
                  <p className="mt-1 mb-6 text-sm leading-6 text-gray-900 sm:mt-2">
                    Search and retrieve detailed nutritional information across
                    a vast selection of foods, recipes, brands, supplements, and
                    more!
                  </p>
                  <img
                    src={NutritionAPICardImage}
                    alt="Nutrition API"
                    className="object-cover rounded-md"
                  />
                  <div className="mt-auto pt-4">
                    <Button
                      text={
                        invalidNutritionAPISubscription
                          ? "Subscription Issue"
                          : nutritionAPIData.isSubscribed
                          ? "Product Details"
                          : "Subscribe"
                      }
                      outlined
                      fullWidth
                      isWarning={invalidNutritionAPISubscription}
                      isLoading={isWaiting}
                      disabled={isWaiting}
                      onClick={() => {
                        invalidNutritionAPISubscription
                          ? setInvalidSubscriptionModalIsOpen(true)
                          : nutritionAPIData.isSubscribed
                          ? navigate(`/details?product=${NUTRITION_API_SLUG}`)
                          : navigate(
                              `/manage-subscription?product=${NUTRITION_API_SLUG}`
                            );
                      }}
                    />
                  </div>
                </Card>
              )}

              {/* Legacy: Nutrition Advisor AI */}
              {displayIndividualLegacySubs && (
                <Card className="col-span-2">
                  <p className="font-extrabold">Nutrition Advisor AI</p>
                  <span className="text-sm text-gray-500 pt-1 pb-2">
                    (RESTful) Conversational Nutrition Advisor
                  </span>
                  <p className="mt-1 mb-6 text-sm leading-6 text-gray-900 sm:mt-2">
                    Get personalized eating advice, explore tailored recipes,
                    and effortlessly connect to our extensive food database.
                  </p>
                  <div
                    style={{
                      position: "relative",
                      width: "100%",
                      height: 0,
                      paddingTop: `${(652 / 1158) * 100}%`,
                    }}
                    className="rounded-md overflow-hidden"
                  >
                    <img
                      src={NutritionAdvisorCardImage}
                      alt="Nutrition Advisor"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                  <div className="mt-auto pt-4">
                    <Button
                      text={
                        invalidNutritionAdvisorSubscription
                          ? "Subscription Issue"
                          : nutritionAdvisorData.isSubscribed
                          ? "Product Details"
                          : "Subscribe"
                      }
                      outlined
                      fullWidth
                      isWarning={invalidNutritionAdvisorSubscription}
                      isLoading={isWaiting}
                      disabled={isWaiting}
                      onClick={() => {
                        invalidNutritionAdvisorSubscription
                          ? setInvalidSubscriptionModalIsOpen(true)
                          : nutritionAdvisorData.isSubscribed
                          ? navigate(
                              `/details?product=${NUTRITION_ADVISOR_SLUG}`
                            )
                          : navigate(
                              `/manage-subscription?product=${NUTRITION_ADVISOR_SLUG}`
                            );
                      }}
                    />
                  </div>
                </Card>
              )}

              {/* Mobile AI Platform (hidden until enabled) */}
              {showMobileAIPlatform && (
                <Card className="col-span-2">
                  <p className="font-extrabold">Mobile AI Platform</p>
                  <span className="text-sm text-gray-500 pt-1 pb-2">
                    Browser-based collaborative AI platform
                  </span>
                  <p className="mt-1 mb-6 text-sm leading-6 text-gray-900 sm:mt-2">
                    Create custom computer-vision SDKs in your browser — from
                    quick, no-training solutions to advanced, collaborative
                    projects!
                  </p>
                  <img
                    src={MobileAICardImage}
                    alt="Mobile AI"
                    className="object-cover rounded-md"
                  />
                  <div className="mt-auto pt-4">
                    <Button
                      text={
                        invalidMobileSubscription
                          ? "Subscription Issue"
                          : mobileData.isSubscribed
                          ? "Product Details"
                          : "Subscribe"
                      }
                      outlined
                      fullWidth
                      isWarning={invalidMobileSubscription}
                      isLoading={isWaiting}
                      disabled={isWaiting}
                      onClick={() => {
                        invalidMobileSubscription
                          ? setInvalidSubscriptionModalIsOpen(true)
                          : mobileData.isSubscribed
                          ? navigate(`/details?product=${MOBILE_AI_SLUG}`)
                          : navigate(
                              `/manage-subscription?product=${MOBILE_AI_SLUG}`
                            );
                      }}
                    />
                  </div>
                </Card>
              )}

              {/* Remodel AI */}
              {flags["remodel-ai-purchase"] && (
                <Card className="col-span-2">
                  <p className="font-extrabold">Remodel AI</p>
                  <span className="text-sm text-gray-500 pt-1 pb-2">
                    3D home scanning and AR paint preview SDK
                  </span>
                  <p className="mt-1 mb-6 text-sm leading-6 text-gray-900 sm:mt-2">
                    Unlock the power of augmented reality — scan, design, and
                    enhance your space with our comprehensive AR toolkit!
                  </p>
                  <img
                    src={RemodelAICardImage}
                    alt="Remodel AI"
                    className="object-cover rounded-md"
                  />
                  <div className="mt-auto pt-4">
                    <Button
                      text={
                        invalidRemodelAISubscription
                          ? "Subscription Issue"
                          : remodelAIData.isSubscribed
                          ? "Product Details"
                          : "Subscribe"
                      }
                      outlined
                      fullWidth
                      isWarning={invalidRemodelAISubscription}
                      isLoading={isWaiting}
                      disabled={isWaiting}
                      onClick={() => {
                        invalidRemodelAISubscription
                          ? setInvalidSubscriptionModalIsOpen(true)
                          : remodelAIData.isSubscribed
                          ? navigate(`/details?product=${REMODEL_AI_SLUG}`)
                          : navigate(
                              `/manage-subscription?product=${REMODEL_AI_SLUG}`
                            );
                      }}
                    />
                  </div>
                </Card>
              )}

              {/* VR Showroom */}
              {flags["vr-showroom-purchase"] && (
                <Card className="col-span-2">
                  <p className="font-extrabold">VR Showroom</p>
                  <span className="text-sm text-gray-500 pt-1 pb-2">
                    VR SDK for product showcase
                  </span>
                  <p className="mt-1 mb-6 text-sm leading-6 text-gray-900 sm:mt-2">
                    Transform your product showcase with our SDK — create
                    immersive VR showrooms in a virtual demo space!
                  </p>
                  <img
                    src={VRShowroomCardImage}
                    alt="VR Showroom"
                    className="object-cover rounded-md"
                  />
                  <div className="mt-auto pt-4">
                    <Button
                      text={
                        invalidVRShowroomSubscription
                          ? "Subscription Issue"
                          : vrShowroomData.isSubscribed
                          ? "Product Details"
                          : "Subscribe"
                      }
                      outlined
                      fullWidth
                      isWarning={invalidVRShowroomSubscription}
                      isLoading={isWaiting}
                      disabled={isWaiting}
                      onClick={() => {
                        invalidVRShowroomSubscription
                          ? setInvalidSubscriptionModalIsOpen(true)
                          : vrShowroomData.isSubscribed
                          ? navigate(`/details?product=${VR_SHOWROOM_SLUG}`)
                          : navigate(
                              `/manage-subscription?product=${VR_SHOWROOM_SLUG}`
                            );
                      }}
                    />
                  </div>
                </Card>
              )}
            </div>
          </>
        )}

        <Modal
          isOpen={invalidSubscriptionModalIsOpen}
          setOpen={(e) => setInvalidSubscriptionModalIsOpen(e)}
          title={"Invalid Subscription Status"}
          disabled={isWaiting}
          isLoading={isWaiting}
          isCancelButton={false}
          description={`Please contact support@passiolife.com to resolve this issue.`}
          submitButtonName={"Contact Passio Support"}
          buttonType={"warning"}
          type={"error"}
          onSubmitted={() =>
            window.open("mailto:support@passiolife.com", "_blank")
          }
        />
      </Container>
    </NavHeader>
  );
};

export default Dashboard;
